import React, { useCallback, useMemo, useState } from "react";
import styles from "./shift-details-stock-inventory.module.scss";
import { fuelSalesColumn } from "./shift-details-stock-inventory-column";
import { Button, Text } from "components/commons";
import { formatAmount, formatVolume, truncate } from "utils";
import { find, sortBy, sumBy } from "lodash";
import classNames from "classnames";
import locale from "localization";
import { prettifyProductCode } from "utils/pretty.utils";
import { isProductExistInUGT } from "../../shift-records.mapper";
import TooltipBubble from "components/commons/tooltip/tooltip-bubble.module";
import Big from "big.js";

const maxValue = (value, MAX = "999999999999999.999") => {
  value = String(value);
  return value === "1000000000000000" ? MAX : value;
};

function calculateTotalizerVariance(beginningTotalizer, endingTotalizer, salesInLiters) {
  const actualDifference = endingTotalizer - beginningTotalizer;
  const variance = salesInLiters - actualDifference;
  const variancePercentage = actualDifference !== 0 ? (variance / actualDifference) * 100 : 0;
  const isNegative = variance < 0;

  const truncatedPercentage = Math.trunc(variancePercentage * 100) / 100;

  return {
    variance: variance.toFixed(3),
    variancePercentage: truncatedPercentage.toFixed(2),
    isNegative: isNegative,
    formattedDisplay: `${variance.toFixed(3)} L (${truncatedPercentage.toFixed(2)}%)`,
    color: isNegative ? "red" : "black",
  };
}

function nameToSku(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");
}

const computeTotalAmount = (t, shift) => {
  let pumpDiscountVat12Percent = t.quantity * t.pumpDiscountPerItem;

  if (t.vat === 0) {
    pumpDiscountVat12Percent = pumpDiscountVat12Percent / 1.12;
  } else {
    pumpDiscountVat12Percent = 0;
  }

  const pricePerUnit =
    t.type === "fuel"
      ? shift.shift.pricing.find((p) => p.product === nameToSku(t.name))?.pricePerLiter || 0
      : t?.originalPricePerItem || 0;

  let grossTotalAmount =
    t.vat === 0
      ? t.quantity * pricePerUnit - pumpDiscountVat12Percent
      : t.grossTotalAmount - t.totalPumpDiscount;

  // from manual input
  if (Math.abs(grossTotalAmount - t.grossTotalAmount) <= 0.1) {
    grossTotalAmount =
      truncate(t.grossTotalAmount, 2) -
      (t.totalDiscount > 0 && t.discountValue > 0 ? t.totalDiscount : 0);
  }

  return (
    Big(grossTotalAmount || 0)
      // .plus(totalPumpDiscount)
      .round(2)
      .toNumber()
  );
};

const computeDiscount = (t, shift) => {
  if (t.totalDiscount > 0 && t.discountValue > 0) {
    return t.totalDiscount;
  }

  const pumpNumber = Number(String(t.pump).split(" ")[1]);
  const pumpDiscount = shift.shiftDiscounts.find(
    (discount) =>
      discount.product === nameToSku(t.name) && Number(discount.pumpSlot) === pumpNumber,
  );
  const isNoDiscountForItem = t?.discount === "" && (t?.pumpDiscountPerItem ?? 0) === 0;
  // STOCQ-5886: Liters Input stores per-liter pump discount via t.pumpDiscountPerItem.
  // The previous logic only computed effectivePumpDiscount from shiftDiscounts when
  // BOTH per-item and totalPumpDiscount were zero, so Liters Input rows ended up with
  // effectivePumpDiscount=0 and the residual leaked into Discount/Premium.
  const effectivePumpDiscount = (() => {
    if ((t.pumpDiscountPerItem ?? 0) > 0) {
      const perLiter = t.vat === 0 ? t.pumpDiscountPerItem / 1.12 : t.pumpDiscountPerItem;
      return Big(perLiter)
        .times(t.quantity || 0)
        .round(2)
        .toNumber();
    }
    if ((t.totalPumpDiscount ?? 0) > 0) {
      return t.totalPumpDiscount;
    }
    if (!isNoDiscountForItem && (pumpDiscount?.discountPerLiter ?? 0) > 0) {
      return t.vat === 0
        ? Big(pumpDiscount.discountPerLiter)
            .div(1.12)
            .times(t.quantity || 0)
            .round(2)
            .toNumber()
        : Big(pumpDiscount.discountPerLiter)
            .times(t.quantity || 0)
            .round(2)
            .toNumber();
    }
    return 0;
  })();
  let totalPromoDiscount =
    t.vat === 0
      ? 0
      : (t.discount || (pumpDiscount?.discountPerLiter ?? 0) === 0) &&
          t.pumpDiscountPerItem === 0 &&
          t.totalDiscount > 0
        ? Math.max(0, t.totalDiscount - effectivePumpDiscount)
        : 0;

  totalPromoDiscount = t.discountValue < 0 ? -Math.abs(totalPromoDiscount) : totalPromoDiscount;
  // STOCQ-5883: when only a pump discount is applied, the per-item totalDiscount
  // mirrors the pump amount with a negative sign. Offset the pump portion so the
  // residual reflects only a true premium (negative discount/premium).
  const adjustedAbsPremium = Math.max(0, Math.abs(t.totalDiscount ?? 0) - effectivePumpDiscount);
  const premiumDiscount = (t.totalDiscount ?? 0) < 0 ? -adjustedAbsPremium : 0;

  return totalPromoDiscount + premiumDiscount;
};

const isTxnPremium = (t) =>
  Math.abs(t.totalDiscount ?? 0) > 0.005 &&
  (t.pumpDiscountPerItem ?? 0) === 0 &&
  (t.totalPumpDiscount ?? 0) === 0 &&
  (t.discount == null || t.discount === "") &&
  Math.abs((t.grossTotalAmount ?? 0) - (t.netTotalAmount ?? 0)) < 0.01;

export const getProductPumpsWithTransactions = ({ productCode = "", currentShift }) => {
  const transactions =
    currentShift?.transactionItem?.[`${productCode}Transactions`]?.filter((transaction) =>
      transaction.pump.includes("Pump"),
    ) || [];
  if (
    transactions.length === 0
      ? true
      : !isProductExistInUGT(productCode, currentShift?.station?.stationUgt)
  ) {
    return [];
  }

  const productCodeTransactionItem = sortBy(
    currentShift?.transactionItem?.[`${productCode}TransactionItem`] || [],
    "metadata",
  )
    .map((t) => ({
      ...t,
      productCode: productCode,
      productName: prettifyProductCode(productCode),
    }))
    .filter((t) => t.pump.includes("Pump"));

  return productCodeTransactionItem
    .filter((productCodeTransaction) => productCodeTransaction.pump !== "Pump 02")
    .map((productCodeTransaction) => {
      const pumpNumber = Number(String(productCodeTransaction.pump).split(" ")[1]);
      const pumpDiscount = currentShift.shiftDiscounts.find(
        (discount) =>
          discount.product === productCodeTransaction.productCode &&
          Number(discount.pumpSlot) === pumpNumber,
      );
      const pumpSaleTransactions = transactions
        .filter(
          (t) =>
            t.pump === productCodeTransaction.pump &&
            !t.transaction.adjustmentType &&
            t.transaction.status === "sale",
        )
        .reduce(
          (acc, t) => {
            const computedTotalAmount = computeTotalAmount(t, currentShift);
            const computedDiscount = computeDiscount(t, currentShift);
            return {
              transactionAmount: acc.transactionAmount + t.transaction.amount,
              amount: acc.amount + t.amount,
              quantity: acc.quantity + t.quantity,
              grossTotalAmount: acc.grossTotalAmount + t.grossTotalAmount,
              netTotalAmount: acc.netTotalAmount + t.netTotalAmount,
              originalTotalAmount: acc.originalTotalAmount + t.originalTotalAmount,
              pumpDiscountPerItem: acc.pumpDiscountPerItem + t.pumpDiscountPerItem,
              totalPumpDiscount: acc.totalPumpDiscount + t.totalPumpDiscount,
              totalDiscount: acc.totalDiscount + t.totalDiscount,
              vatExemptAmount:
                acc.vatExemptAmount +
                (t.vat === 0 &&
                t.originalTotalAmount - Math.abs(t.totalDiscount) - t.netTotalAmount > 0.005
                  ? -(t.originalTotalAmount - Math.abs(t.totalDiscount) - t.netTotalAmount)
                  : 0),
              vat: acc.vat + (t.vat || 0),
              vatExempt: acc.vatExempt + (t.vatExempt || 0),
              vatExempt12percent: acc.vatExempt12percent + t.vatExempt12percent,
              discountValue: acc.discountValue + t.discountValue,

              //computed
              computedTotalAmount: acc.computedTotalAmount + computedTotalAmount,
              computedDiscount: acc.computedDiscount + computedDiscount,
              computedPremium:
                acc.computedPremium + (isTxnPremium(t) ? Math.abs(t.totalDiscount || 0) : 0),
              computedDiscountOnly:
                acc.computedDiscountOnly + (isTxnPremium(t) ? 0 : computedDiscount),
            };
          },
          {
            transactionAmount: 0,
            amount: 0,
            quantity: 0,
            grossTotalAmount: 0,
            netTotalAmount: 0,
            originalTotalAmount: 0,
            pumpDiscountPerItem: 0,
            totalPumpDiscount: 0,
            totalDiscount: 0,
            vatExemptAmount: 0,
            vat: 0,
            vatExempt: 0,
            vatExempt12percent: 0,
            discountValue: 0,
            computedTotalAmount: 0,
            computedDiscount: 0,
            computedPremium: 0,
            computedDiscountOnly: 0,
          },
        );

      let totalizer = {
        oldBeginningTotalizer:
          productCodeTransaction.oldBeginningTotalizer || productCodeTransaction.beginningTotalizer,
        oldEndingTotalizer:
          productCodeTransaction.oldEndingTotalizer || productCodeTransaction.endingTotalizer,
        oldTotalizerDifference:
          productCodeTransaction.oldTotalizerDifference ||
          productCodeTransaction.totalizerDifference,
      };

      return {
        ...productCodeTransaction,
        ...totalizer,
        transactionAmount: pumpSaleTransactions.transactionAmount,
        amount: pumpSaleTransactions.amount,
        quantity: pumpSaleTransactions.quantity,
        grossTotalAmount: pumpSaleTransactions.grossTotalAmount,
        netTotalAmount: pumpSaleTransactions.netTotalAmount,
        originalTotalAmount: pumpSaleTransactions.originalTotalAmount,
        pumpDiscountPerItem: pumpDiscount?.discountPerLiter || 0,
        isAmountInput: pumpSaleTransactions.pumpDiscountPerItem === 0,
        totalPumpDiscount: pumpSaleTransactions.totalPumpDiscount,
        totalDiscount: pumpSaleTransactions.totalDiscount,
        vatExemptAmount: pumpSaleTransactions.vatExemptAmount,
        vatExempt12percent: pumpSaleTransactions.vatExempt12percent,
        vat: pumpSaleTransactions.vat,
        vatExempt: pumpSaleTransactions.vatExempt,
        discountValue: pumpSaleTransactions.discountValue,
        //computed
        computedTotalAmount: pumpSaleTransactions.computedTotalAmount,
        computedDiscount: pumpSaleTransactions.computedDiscount,
        computedPremium: pumpSaleTransactions.computedPremium,
        computedDiscountOnly: pumpSaleTransactions.computedDiscountOnly,
      };
    });
};

const FuelSummary = ({ shift, data, transactions, name, sku }) => {
  const isConnectedToPts = shift?.stationCheck?.isConnectedToPts || false;
  const [remarks, setRemarks] = useState("");
  const stationPumps = useMemo(
    () => shift.shiftPumpTotals.filter((pump) => pump.fuelingPointId !== "Pump 02") || [],
    [shift],
  );
  const pumpDiscounts = useMemo(() => shift.shiftDiscounts || [], [shift]);

  const productPumps = useMemo(() => {
    let pumpWithTransactions = getProductPumpsWithTransactions({
      productCode: sku,
      currentShift: shift,
    });

    const clonePumpTotalizer = pumpWithTransactions[0];
    const stationPumpsWithoutTransactions = stationPumps
      .filter(
        (pump) => !pumpWithTransactions.some((pt) => pt.shiftPumpTotalId === pump.shiftPumpTotalId),
      )
      .map((pt) => {
        const beginningTotalizer = pt[`startTotalizer${name.replace(" ", "")}`];
        const endingTotalizer = pt[`endTotalizer${name.replace(" ", "")}`];
        const oldBeginningTotalizer = pt[`startTotalizer${name.replace(" ", "")}Old`];
        const oldEndingTotalizer = pt[`endTotalizer${name.replace(" ", "")}Old`];
        const pumpNumber = Number(String(pt.fuelingPointId).split(" ")[1]);
        const pumpDiscount = pumpDiscounts.find(
          (discount) => discount.product === sku && Number(discount.pumpSlot) === pumpNumber,
        );
        const stocqBeginningTotalizer = oldBeginningTotalizer || beginningTotalizer;
        const stocqEndingTotalizer = oldEndingTotalizer || endingTotalizer;
        let totalizer = {
          oldBeginningTotalizer: isConnectedToPts ? stocqBeginningTotalizer : 0,
          oldEndingTotalizer: isConnectedToPts ? stocqEndingTotalizer : 0,
          oldTotalizerDifference: isConnectedToPts
            ? stocqEndingTotalizer - stocqBeginningTotalizer
            : 0,
        };

        return {
          ...clonePumpTotalizer,
          shiftPumpTotalId: pt.shiftPumpTotalId,
          metadata: `${pt.fuelingPointId}, ${pt.fuelingPointId}`,
          pump: pt.fuelingPointId,
          sku: sku,
          quantity: 0,
          pumpDiscountPerItem: pumpDiscount?.discountPerLiter || 0,
          amount: 0,
          totalPumpDiscount: 0,
          originalTotalAmount: 0,
          netTotalAmount: 0,
          totalizerDifference: 0,
          totalizerVariance: 0,
          beginningTotalizer: beginningTotalizer,
          endingTotalizer: endingTotalizer,
          oldTotalizerVariance: 0,
          vatExemptAmount: 0,
          vat: 0,
          vatExempt: 0,
          vatExempt12percent: 0,
          discountValue: 0,
          //computed
          computedTotalAmount: 0,
          computedDiscount: 0,
          computedPremium: 0,
          computedDiscountOnly: 0,
          ...totalizer,
        };
      });

    pumpWithTransactions = [...pumpWithTransactions, ...stationPumpsWithoutTransactions];

    pumpWithTransactions = pumpWithTransactions.map((t) => {
      const pumpNumber = Number(String(t.pump).split(" ")[1]);
      return {
        ...t,
        pumpNumber,
      };
    });

    return sortBy(pumpWithTransactions, ["pumpNumber"]);
  }, [sku, shift, stationPumps, name, pumpDiscounts, isConnectedToPts]);

  const totalSalesInLiters = sumBy(productPumps, "quantity") || 0;

  const getPrice = useCallback(() => {
    return find(shift.shift.pricing, { product: sku })?.pricePerLiter || 0;
  }, [shift.shift.pricing, sku]);

  const computeTotalDiscount = (items) => {
    return sumBy(items, "computedDiscount") || 0;
  };

  const totalAmount = sumBy(productPumps, "computedTotalAmount") || 0;
  const totalPremium = sumBy(productPumps, "computedPremium") || 0;
  const totalDiscountOnly = sumBy(productPumps, "computedDiscountOnly") || 0;
  const totalVatExempt =
    sumBy(transactions, (item) => {
      return item.vatExempt12percent;
    }) || 0;
  let totalDiscount = computeTotalDiscount(productPumps) + totalVatExempt;
  const totalFinalAmount = totalAmount + totalDiscount;
  const totalizerDifference = sumBy(productPumps, "totalizerDifference").toFixed(3) || 0;

  const { variance: totalizerVariance, variancePercentage: totalizerVariancePercentage } =
    calculateTotalizerVariance(
      sumBy(productPumps, "beginningTotalizer") || 0,
      sumBy(productPumps, "endingTotalizer") || 0,
      sumBy(productPumps, "quantity") || 0,
    );

  const oldTotalizerDifference = sumBy(productPumps, "oldTotalizerDifference") || 0 || 0;

  const { variance: oldTotalizerVariance, variancePercentage: oldTotalizerVariancePercentage } =
    calculateTotalizerVariance(
      sumBy(productPumps, "oldBeginningTotalizer") || 0,
      sumBy(productPumps, "oldEndingTotalizer") || 0,
      sumBy(productPumps, "quantity") || 0,
    );

  const extractPumpNumber = (metadata) => {
    const match = metadata.match(/Pump (\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };

  const getPumpTotalizerRemarks = (shiftPumpTotalId, productName = "") => {
    productName = productName.replace(" ", "");
    return shift.shiftPumpTotals?.length > 0
      ? shift.shiftPumpTotals.find(
          (totalizer) => totalizer.shiftPumpTotalId === shiftPumpTotalId,
        )?.[`remarks${productName}`]
      : "";
  };

  return (
    <>
      <div className={styles.fuelSummaryWrapper}>
        <div className={styles.tableHeader}>
          <Text className={styles.label}>{name}</Text>
        </div>
        <div className={styles.tableScrollContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {fuelSalesColumn.map((item, i) => {
                  return <th key={i}>{item.text}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {productPumps.length !== 0 &&
                productPumps.map((item, i) => {
                  const totalizerRemarks = getPumpTotalizerRemarks(item.shiftPumpTotalId, name);
                  const {
                    variance: totalizerVariance,
                    variancePercentage: totalizerVariancePercentage,
                  } = calculateTotalizerVariance(
                    item.beginningTotalizer,
                    item.endingTotalizer,
                    item.quantity,
                  );

                  const {
                    variance: oldTotalizerVariance,
                    variancePercentage: oldTotalizerVariancePercentage,
                  } = calculateTotalizerVariance(
                    item.oldBeginningTotalizer,
                    item.oldEndingTotalizer,
                    item.quantity,
                  );

                  const pumpPrice = getPrice(item.sku) ?? item.pumpPricePerItem ?? 0;
                  // const totalDiscount = computeTotalDiscount(sortSaleTransactions(item.pump));
                  // const finalAmountPaid = totalAmount - totalDiscount;
                  // const totalOtherDiscount = finalAmountPaid - totalAmount || 0;

                  let pumpNumber = 1;
                  if (item.metadata !== undefined) {
                    pumpNumber = extractPumpNumber(item.metadata);
                  }

                  const totalAmount = item.computedTotalAmount;
                  const vatExemptAmount = item.vatExempt12percent;
                  let totalDiscount = item.computedDiscount + vatExemptAmount;
                  const finalAmountPaid = totalAmount + totalDiscount;

                  const itemPremium = item.computedPremium || 0;
                  const rowIsPremium = itemPremium > 0.005;

                  return (
                    <tr key={i}>
                      <td>
                        {/* pump number */}
                        <Text className={styles.content}>{pumpNumber ?? "--"}</Text>
                      </td>
                      <td>
                        {/* pump price */}
                        <Text className={styles.content}>{formatAmount(pumpPrice)}</Text>
                      </td>
                      <td>
                        <Text className={styles.content}>
                          {formatAmount(item.pumpDiscountPerItem ?? 0)}
                        </Text>
                      </td>
                      <td>
                        {/* beginning totalizer */}
                        <Text className={styles.content}>
                          {formatVolume(maxValue(item.beginningTotalizer) ?? 0)}
                        </Text>
                        <Text className={styles.subcontent}>
                          {formatVolume(maxValue(item.oldBeginningTotalizer) ?? 0)}
                        </Text>
                      </td>
                      <td>
                        {/* ending totalizer */}
                        <Text className={styles.content}>
                          {formatVolume(maxValue(item.endingTotalizer) ?? 0)}
                        </Text>
                        <Text className={styles.subcontent}>
                          {formatVolume(maxValue(item.oldEndingTotalizer) ?? 0)}
                        </Text>
                      </td>
                      <td>
                        {/* recorded volume */}
                        <Text className={styles.content}>{formatVolume(item.quantity ?? 0)}</Text>
                      </td>
                      <td>
                        {/* amount */}
                        <Text className={styles.spacer100}>{formatAmount(totalAmount)}</Text>
                      </td>
                      <td>
                        {/* discount/premium */}
                        {(() => {
                          if (rowIsPremium && itemPremium > 0.005) {
                            // STOCQ-6204: when a premium and a txn discount coexist on the same pump,
                            // show the NET = premium − discount. itemPremium (computedPremium) is the
                            // sign-convention-independent premium magnitude from STOCQ-6194; subtract
                            // only the NON-premium discount contribution (computedDiscountOnly, signed
                            // negative for a real discount) so old- and new-convention premiums both
                            // render correctly. Premium-only rows are unchanged (computedDiscountOnly = 0).
                            const netPremium = itemPremium + (item.computedDiscountOnly ?? 0);
                            const isNetDiscount = netPremium < -0.005;
                            return (
                              <Text className={isNetDiscount ? styles.negative : styles.content}>
                                {isNetDiscount ? "-" : ""}
                                {formatAmount(Math.abs(netPremium))}
                              </Text>
                            );
                          }
                          const hasNonZeroDiscount = Math.abs(totalDiscount ?? 0) > 0.005;
                          return (
                            <Text className={hasNonZeroDiscount ? styles.negative : styles.content}>
                              {hasNonZeroDiscount ? "-" : ""}
                              {formatAmount(Math.abs(totalDiscount ?? 0))}
                            </Text>
                          );
                        })()}
                      </td>
                      <td>
                        {/* final amount */}
                        <Text className={styles.spacer100}>
                          {formatAmount(finalAmountPaid ?? 0)}
                        </Text>
                      </td>

                      <td>
                        {/* totalizer difference */}
                        <Text className={styles.content}>
                          {formatVolume(item.totalizerDifference ?? 0)}
                        </Text>
                        <Text className={styles.subcontent}>
                          {formatVolume(item.oldTotalizerDifference ?? 0)}
                        </Text>
                      </td>
                      <td>
                        {/* totalizer variance */}
                        <Text
                          className={classNames(
                            styles.content,
                            totalizerVariance > 0 && styles.positive,
                            totalizerVariance < 0 && styles.negative,
                          )}
                        >
                          {`${formatVolume(totalizerVariance ?? 0)} (${
                            item.totalizerDifference === 0
                              ? "N/A"
                              : totalizerVariancePercentage + "%"
                          })`}
                        </Text>
                        <Text
                          className={classNames(
                            styles.subcontent,
                            oldTotalizerVariance > 0 && styles.positive,
                            oldTotalizerVariance < 0 && styles.negative,
                          )}
                        >
                          {`${formatVolume(oldTotalizerVariance ?? 0)} (${
                            item.oldTotalizerDifference === 0
                              ? "N/A"
                              : oldTotalizerVariancePercentage + "%"
                          })`}
                        </Text>
                      </td>
                      <td>
                        {totalizerRemarks ? (
                          <TooltipBubble content={remarks} disabled={false}>
                            <Button
                              style={{
                                textDecoration: "none",
                                background: "none",
                              }}
                              link
                              onClick={() => {
                                setRemarks(totalizerRemarks);
                              }}
                            >
                              {locale.view}
                            </Button>
                          </TooltipBubble>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              {productPumps.length !== 0 && (
                <tr>
                  <td>
                    <Text className={styles.label}>{"Total"}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.label}>{formatVolume(totalSalesInLiters)}</Text>
                  </td>
                  <td>
                    <Text className={styles.label}>{formatAmount(totalAmount)}</Text>
                  </td>
                  <td>
                    {(() => {
                      if (totalPremium > 0.005) {
                        // STOCQ-6204: show the NET = premium − discount. totalPremium is the
                        // convention-independent premium magnitude; subtract only the non-premium
                        // discount contribution (totalDiscountOnly, signed) so the totals cell matches
                        // the per-row cells. Premium-only totals are unchanged (totalDiscountOnly = 0).
                        const netPremium = totalPremium + totalDiscountOnly;
                        const isNetDiscount = netPremium < -0.005;
                        return (
                          <Text className={isNetDiscount ? styles.negative : styles.content}>
                            {isNetDiscount ? "-" : ""}
                            {formatAmount(Math.abs(netPremium))}
                          </Text>
                        );
                      }
                      const hasNonZeroDiscount = Math.abs(totalDiscount ?? 0) > 0.005;
                      return (
                        <Text className={hasNonZeroDiscount ? styles.negative : styles.content}>
                          {hasNonZeroDiscount ? "-" : ""}
                          {formatAmount(Math.abs(totalDiscount ?? 0))}
                        </Text>
                      );
                    })()}
                  </td>
                  <td>
                    <Text className={styles.label}>{formatAmount(totalFinalAmount)}</Text>
                  </td>

                  <td>
                    <Text className={styles.label}>{formatVolume(totalizerDifference)}</Text>
                    <Text className={styles.subcontent}>
                      {formatVolume(oldTotalizerDifference ?? 0)}
                    </Text>
                  </td>
                  <td>
                    <Text
                      className={classNames(
                        styles.content,
                        totalizerVariance > 0 && styles.positive,
                        totalizerVariance < 0 && styles.negative,
                      )}
                    >
                      {`${formatVolume(totalizerVariance ?? 0)} (${
                        totalizerDifference === 0 ? "N/A" : totalizerVariancePercentage + "%"
                      })`}
                    </Text>
                    <Text
                      className={classNames(
                        styles.subcontent,
                        oldTotalizerVariance > 0 && styles.positive,
                        oldTotalizerVariance < 0 && styles.negative,
                      )}
                    >
                      {`${formatVolume(oldTotalizerVariance ?? 0)} (${
                        oldTotalizerDifference === 0 ? "N/A" : oldTotalizerVariancePercentage + "%"
                      })`}
                    </Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!productPumps.length && (
          <div className={styles.tableRow}>
            <Text className={styles.label}>{"No Results"}</Text>
          </div>
        )}
      </div>
    </>
  );
};

export default FuelSummary;
